// Landing critters: little paper characters – one for each thing the app
// makes, in the colour of its word in the headline (and the booklet, which
// has no word there, in the pink it wears on the dashboard) – that potter
// along the top of the subject ticker and nap on the closing CTA. Any page can
// also perch one on an edge with <canvas class="lp-critters-perch"
// data-kind="quiz">: it stands there, watches the pointer, and hops when the
// pointer comes close or it is poked.
//
// Pure decoration. Both canvases are aria-hidden and pointer-events:none;
// clicks are hit-tested from a window listener instead, so nothing on the page
// ever loses a click to a critter. Under reduced motion they are drawn once,
// standing still, and never move.
(function () {
    'use strict';

    const heroCanvas = document.getElementById('lp-critters');
    const napCanvas = document.getElementById('lp-critters-nap');
    const perchCanvases = Array.from(document.querySelectorAll('.lp-critters-perch'));
    if (!heroCanvas && !napCanvas && !perchCanvases.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rand = (a, b) => a + Math.random() * (b - a);
    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

    const INK = '#1e1b2e';
    const CHEEK = 'rgba(255, 120, 140, 0.4)';
    const CONFETTI = ['#3660ff', '#00c78d', '#f4b447', '#8657d1', '#fffdf8'];
    const FONT = '900 15px Nunito, -apple-system, "Segoe UI", sans-serif';

    // Keyed by the flip word each one stands for.
    const KINDS = {
        presentation: { fill: '#3660ff', w: 48, h: 34 },
        worksheet:    { fill: '#00c78d', w: 34, h: 44 },
        booklet:      { fill: '#e0679a', w: 46, h: 36 },
        quiz:         { fill: '#f4b447', w: 38, h: 36 },
        mark:         { fill: '#8657d1', w: 36, h: 36 },
    };
    const ORDER = ['presentation', 'worksheet', 'booklet', 'quiz', 'mark'];

    const LEG = 7;            // the body stands this far off the ground
    const GRAVITY = 1900;     // px/s², pulling down
    const HOP = 430;          // take-off speed of an ordinary hop (~50px high)

    // The hero critters stand on the ticker, which sits 8px below the hero and
    // is rotated -1.2deg about its centre (.lp-ticker in landing.css). The
    // canvas hangs 40px past the hero's foot so the ground can follow the tilt
    // down on the left; everything below the ground is painted over by the
    // ticker, which comes later in the page.
    const HERO_OVERHANG = 40;
    const TICKER_GAP = 8;
    const TICKER_SLOPE = Math.tan(1.2 * Math.PI / 180);

    /* ── Critters ───────────────────────────────────────── */

    function makeCritter(kind, x) {
        return {
            kind, k: KINDS[kind],
            x, y: 0, vx: 0, vy: 0,
            dir: Math.random() < 0.5 ? -1 : 1,
            speed: rand(24, 38),
            state: 'walk', timer: rand(1.5, 4.5),
            gait: rand(0, Math.PI * 2),
            sq: 0, sqv: 0, sqTarget: 0,    // squash (+) / stretch (-), on a spring
            blink: rand(0.5, 4), blinkT: 0,
            look: 0, lookY: 0, idleLook: 0,
            happy: 0,                      // seconds of open-mouthed grin left
            asleep: false, stay: false,    // the napper never wanders off
        };
    }

    // Crouch first, then launch: the anticipation is what sells the jump.
    function hop(c, power, vx) {
        if (c.state === 'crouch' || c.state === 'air') return false;
        c.state = 'crouch';
        c.timer = 0.09;
        c.sqTarget = 0.3;
        c.hopPower = HOP * (power || 1);
        c.vx = vx || 0;
        c.happy = 1;
        return true;
    }

    function stepCritter(c, dt, scene) {
        // Squash spring: underdamped, so a landing wobbles once before settling.
        if (c.asleep) c.sqTarget = Math.sin(scene.t * 1.9) * 0.05;    // breathing
        c.sqv += ((c.sqTarget - c.sq) * 520 - c.sqv * 15) * dt;
        c.sq += c.sqv * dt;

        c.blink -= dt;
        if (c.blink <= 0) {
            c.blinkT = 0.13;
            // Now and then a double blink.
            c.blink = Math.random() < 0.2 ? 0.25 : rand(2, 5.5);
        }
        if (c.blinkT > 0) c.blinkT -= dt;
        if (c.happy > 0) c.happy -= dt;

        // Eyes follow the pointer while it's near; otherwise they look where
        // they're going, or glance about when stood still.
        let tx = c.state === 'idle' ? c.idleLook : c.dir * 0.7;
        let ty = 0;
        const p = scene.pointer;
        if (p) {
            const dx = p.x - c.x;
            const dy = p.y - (scene.groundAt(c.x) - c.y - LEG - c.k.h * 0.55);
            const d = Math.hypot(dx, dy) || 1;
            if (d < 420) { tx = dx / d; ty = dy / d; }
        }
        const ease = Math.min(1, dt * 10);
        c.look += (tx - c.look) * ease;
        c.lookY += (ty - c.lookY) * ease;

        switch (c.state) {
            case 'walk':
                c.x += c.dir * c.speed * dt;
                c.gait += dt * c.speed * 0.3;
                c.timer -= dt;
                if (c.timer <= 0) {
                    if (Math.random() < 0.25) hop(c, rand(0.7, 1));
                    else {
                        c.state = 'idle';
                        c.timer = rand(0.8, 2.8);
                        c.idleLook = rand(-1, 1);
                    }
                }
                break;
            case 'idle':
                if (c.stay) break;
                c.timer -= dt;
                if (c.timer < 1 && Math.random() < dt) c.idleLook = rand(-1, 1);
                if (c.timer <= 0) {
                    if (Math.random() < 0.4) c.dir *= -1;
                    c.state = 'walk';
                    c.timer = rand(2, 6);
                }
                break;
            case 'crouch':
                c.timer -= dt;
                if (c.timer <= 0) {
                    c.state = 'air';
                    c.vy = c.hopPower;
                    c.sqTarget = 0;
                    c.sqv -= 7;                 // stretch on the way up
                }
                break;
            case 'air':
                c.y += c.vy * dt;
                c.vy -= GRAVITY * dt;
                c.x += c.vx * dt;
                if (c.y <= 0) {
                    c.sqv += 3 + c.hopPower / 110;   // harder landing, bigger squash
                    c.y = 0; c.vy = 0; c.vx = 0;
                    c.state = c.stay ? 'idle' : 'walk';
                    c.timer = rand(1.5, 4);
                }
                break;
        }

        const edge = c.k.w / 2 + 8;
        if (c.x < edge) { c.x = edge; c.dir = 1; c.vx = Math.abs(c.vx); }
        if (c.x > scene.w - edge) { c.x = scene.w - edge; c.dir = -1; c.vx = -Math.abs(c.vx); }
    }

    // Two walkers about to bump: the one heading into the other either
    // leapfrogs it or thinks better of it and turns round.
    function avoid(critters) {
        for (const a of critters) {
            if (a.state !== 'walk') continue;
            for (const b of critters) {
                if (a === b || b.state === 'air') continue;
                const gap = b.x - a.x;
                if (Math.sign(gap) !== a.dir) continue;
                if (Math.abs(gap) > (a.k.w + b.k.w) / 2 + 10) continue;
                if (Math.random() < 0.55) hop(a, 1.15, a.dir * 115);
                else a.dir *= -1;
                break;
            }
        }
    }

    /* ── Drawing ────────────────────────────────────────── */

    function roundRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    }

    function drawCritter(ctx, c, ground) {
        const { w, h, fill } = c.k;
        const sq = clamp(c.sq, -0.35, 0.4);
        const W = w * (1 + sq * 0.7);
        const H = h * (1 - sq);
        const walking = c.state === 'walk';
        const air = c.state === 'air';
        const bob = walking ? Math.abs(Math.sin(c.gait)) * 2 : 0;

        // Ground shadow, shrinking as it rises.
        const s = Math.max(0.3, 1 - c.y / 110);
        ctx.fillStyle = 'rgba(30, 27, 46, 0.14)';
        ctx.beginPath();
        ctx.ellipse(c.x, ground, W * 0.48 * s, 3.2 * s, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.save();
        ctx.translate(c.x, ground - c.y);
        ctx.strokeStyle = INK;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Legs: shortened by a crouch, tucked up in the air, swinging in step.
        const legLen = air ? LEG * 0.7 : LEG * (1 - Math.max(0, sq) * 1.2);
        const base = -legLen - bob;
        ctx.lineWidth = 2.5;
        for (const side of [-1, 1]) {
            const hipX = side * W * 0.2;
            let footX = hipX, footY = 0;
            if (walking) {
                const ph = c.gait + (side > 0 ? Math.PI : 0);
                footX += Math.sin(ph) * 3.5 * c.dir;
                footY = -Math.max(0, Math.cos(ph)) * 2.5;
            } else if (air) {
                footX += side * 2.5;
                footY = base + legLen;
            }
            ctx.beginPath();
            ctx.moveTo(hipX, base);
            ctx.lineTo(footX, footY);
            ctx.stroke();
        }

        ctx.translate(0, base);
        ctx.lineWidth = 2;
        ctx.fillStyle = fill;
        let faceY;

        if (c.kind === 'presentation') {
            // A slide: landscape, with its title bar.
            roundRect(ctx, -W / 2, -H, W, H, 6);
            ctx.fill(); ctx.stroke();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(-W / 2 + 8, -H + 8);
            ctx.lineTo(-W / 2 + 8 + W * 0.4, -H + 8);
            ctx.stroke();
            ctx.strokeStyle = INK;
            ctx.lineWidth = 2;
            faceY = -H * 0.42;
        } else if (c.kind === 'worksheet') {
            // A worksheet: portrait, dog-eared, ruled.
            const ear = 9;
            ctx.beginPath();
            ctx.moveTo(-W / 2, -H);
            ctx.lineTo(W / 2 - ear, -H);
            ctx.lineTo(W / 2, -H + ear);
            ctx.lineTo(W / 2, 0);
            ctx.lineTo(-W / 2, 0);
            ctx.closePath();
            ctx.fill(); ctx.stroke();
            ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
            ctx.beginPath();
            ctx.moveTo(W / 2 - ear, -H);
            ctx.lineTo(W / 2 - ear, -H + ear);
            ctx.lineTo(W / 2, -H + ear);
            ctx.closePath();
            ctx.fill(); ctx.stroke();
            ctx.strokeStyle = 'rgba(30, 27, 46, 0.35)';
            ctx.lineWidth = 1.5;
            for (const ly of [-7, -12]) {
                ctx.beginPath();
                ctx.moveTo(-W / 2 + 6, ly);
                ctx.lineTo(W / 2 - 6, ly);
                ctx.stroke();
            }
            ctx.strokeStyle = INK;
            ctx.lineWidth = 2;
            faceY = -H * 0.62;
        } else if (c.kind === 'booklet') {
            // A booklet, stood open: two pages dipping to the spine.
            ctx.beginPath();
            ctx.moveTo(0, -H + 6);
            ctx.quadraticCurveTo(-W * 0.25, -H - 1, -W / 2, -H + 2);
            ctx.lineTo(-W / 2, -2);
            ctx.quadraticCurveTo(-W * 0.25, -5, 0, 0);
            ctx.quadraticCurveTo(W * 0.25, -5, W / 2, -2);
            ctx.lineTo(W / 2, -H + 2);
            ctx.quadraticCurveTo(W * 0.25, -H - 1, 0, -H + 6);
            ctx.closePath();
            ctx.fill(); ctx.stroke();
            faceY = -H * 0.52;
            // The spine, broken where the face is.
            ctx.lineWidth = 1.6;
            ctx.beginPath();
            ctx.moveTo(0, -H + 6);
            ctx.lineTo(0, faceY - 6);
            ctx.moveTo(0, faceY + 11);
            ctx.lineTo(0, 0);
            ctx.stroke();
            ctx.lineWidth = 2;
        } else if (c.kind === 'quiz') {
            // A quiz card, with a question mark for a quiff.
            roundRect(ctx, -W / 2, -H, W, H, 12);
            ctx.fill(); ctx.stroke();
            ctx.save();
            ctx.translate(4, -H - 1);
            ctx.rotate(0.25 + Math.sin(c.gait) * 0.12 + c.sqv * 0.01);
            ctx.fillStyle = INK;
            ctx.font = FONT;
            ctx.textAlign = 'center';
            ctx.fillText('?', 0, 0);
            ctx.restore();
            faceY = -H * 0.5;
        } else {
            // A mark: round, with a tick on its tummy.
            ctx.beginPath();
            ctx.ellipse(0, -H / 2, W / 2, H / 2, 0, 0, Math.PI * 2);
            ctx.fill(); ctx.stroke();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.moveTo(-5, -9);
            ctx.lineTo(-1.5, -5.5);
            ctx.lineTo(5, -12);
            ctx.stroke();
            ctx.strokeStyle = INK;
            ctx.lineWidth = 2;
            faceY = -H * 0.62;
        }

        drawFace(ctx, c, faceY, Math.min(W * 0.2, 8));
        ctx.restore();
    }

    function drawFace(ctx, c, fy, ew) {
        const happyAir = c.happy > 0 && c.state === 'air';

        ctx.fillStyle = CHEEK;
        for (const side of [-1, 1]) {
            ctx.beginPath();
            ctx.ellipse(side * (ew + 5), fy + 5, 3, 2, 0, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.lineWidth = 1.6;
        for (const side of [-1, 1]) {
            const ex = side * ew;
            if (c.asleep) {
                ctx.beginPath();
                ctx.arc(ex, fy - 1, 3, 0.15 * Math.PI, 0.85 * Math.PI);
                ctx.stroke();
            } else if (happyAir) {
                ctx.beginPath();
                ctx.arc(ex, fy + 2, 3, 1.15 * Math.PI, 1.85 * Math.PI);
                ctx.stroke();
            } else if (c.blinkT > 0) {
                ctx.beginPath();
                ctx.moveTo(ex - 3, fy);
                ctx.lineTo(ex + 3, fy);
                ctx.stroke();
            } else {
                ctx.fillStyle = '#fff';
                ctx.beginPath();
                ctx.arc(ex, fy, 4.3, 0, Math.PI * 2);
                ctx.fill(); ctx.stroke();
                const px = ex + c.look * 1.7, py = fy + c.lookY * 1.5;
                ctx.fillStyle = INK;
                ctx.beginPath();
                ctx.arc(px, py, 2.2, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = '#fff';
                ctx.beginPath();
                ctx.arc(px + 0.8, py - 0.8, 0.75, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        const my = fy + 6;
        ctx.fillStyle = INK;
        if (c.happy > 0) {
            ctx.beginPath();
            ctx.arc(0, my - 0.5, 3.4, 0, Math.PI);
            ctx.closePath();
            ctx.fill();
        } else if (c.asleep) {
            ctx.beginPath();
            ctx.arc(0, my + 0.5, 1.5 + Math.sin(c.sq * 60) * 0.3, 0, Math.PI * 2);
            ctx.stroke();
        } else {
            ctx.beginPath();
            ctx.arc(0, my - 2, 2.8, 0.2 * Math.PI, 0.8 * Math.PI);
            ctx.stroke();
        }
    }

    /* ── Bits: confetti, sparkles, snores ───────────────── */

    function confetti(scene, x, y, n) {
        for (let i = 0; i < n; i++) {
            const a = rand(-Math.PI * 0.9, -Math.PI * 0.1);
            const v = rand(120, 300);
            scene.bits.push({
                type: 'paper', x, y,
                vx: Math.cos(a) * v, vy: Math.sin(a) * v,
                rot: rand(0, 6), vr: rand(-12, 12),
                color: CONFETTI[i % CONFETTI.length],
                life: rand(0.8, 1.3), age: 0,
            });
        }
    }

    function sparkle(scene, x, y) {
        for (let i = 0; i < 3; i++) {
            scene.bits.push({
                type: 'star', x: x + (i - 1) * 14, y: y - (i === 1 ? 8 : 0),
                vx: (i - 1) * 12, vy: -30,
                life: 0.8, age: -i * 0.06, size: i === 1 ? 6 : 4.5,
            });
        }
    }

    function snore(scene, x, y) {
        scene.bits.push({ type: 'z', x, y, vx: 10, vy: -16, life: 2.6, age: 0, seed: rand(0, 6) });
    }

    function stepBits(scene, dt) {
        scene.bits = scene.bits.filter(b => {
            b.age += dt;
            if (b.age < 0) return true;
            if (b.type === 'paper') {
                b.vy += 700 * dt;
                b.vx *= 1 - dt * 1.5;
                b.rot += b.vr * dt;
            }
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            return b.age < b.life;
        });
    }

    function drawBits(ctx, scene) {
        for (const b of scene.bits) {
            if (b.age < 0) continue;
            const t = b.age / b.life;
            ctx.save();
            ctx.globalAlpha = t > 0.7 ? (1 - t) / 0.3 : 1;
            ctx.translate(b.x, b.y);
            if (b.type === 'paper') {
                ctx.rotate(b.rot);
                ctx.scale(1, Math.cos(b.rot * 1.7));    // tumbling
                ctx.fillStyle = b.color;
                ctx.strokeStyle = INK;
                ctx.lineWidth = 1;
                ctx.fillRect(-3.5, -2, 7, 4);
                ctx.strokeRect(-3.5, -2, 7, 4);
            } else if (b.type === 'star') {
                const r = b.size * (t < 0.3 ? t / 0.3 : 1);
                ctx.fillStyle = '#f4b447';
                ctx.strokeStyle = INK;
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                for (let i = 0; i < 8; i++) {
                    const rr = i % 2 ? r * 0.38 : r;
                    const a = i * Math.PI / 4 - Math.PI / 2;
                    ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
                }
                ctx.closePath();
                ctx.fill(); ctx.stroke();
            } else {
                ctx.translate(Math.sin(b.age * 2.5 + b.seed) * 4, 0);
                ctx.font = FONT.replace('15px', (10 + t * 8).toFixed(1) + 'px');
                ctx.fillStyle = INK;
                ctx.fillText('z', 0, 0);
            }
            ctx.restore();
        }
    }

    /* ── Scenes ─────────────────────────────────────────── */

    function Scene(canvas, opts) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.w = 0; this.h = 0; this.t = 0;
        this.critters = [];
        this.bits = [];
        this.pointer = null;
        this.onScreen = false;
        this.rect = null;
        Object.assign(this, opts);
    }

    Scene.prototype.resize = function () {
        const w = this.canvas.clientWidth;
        const h = this.canvas.clientHeight;
        // Zero while the landing view is display:none (before the auth check,
        // and between sessions); the observer calls back once it has a size.
        if (!w || !h) return false;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.canvas.width = Math.round(w * dpr);
        this.canvas.height = Math.round(h * dpr);
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.w = w; this.h = h;
        this.populate();
        return true;
    };

    Scene.prototype.step = function (dt) {
        this.t += dt;
        for (const c of this.critters) stepCritter(c, dt, this);
        if (this.afterStep) this.afterStep(dt);
        stepBits(this, dt);
    };

    Scene.prototype.draw = function () {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.w, this.h);
        // Airborne critters last, so a leapfrog goes over the top.
        const order = this.critters.slice().sort((a, b) => a.y - b.y);
        for (const c of order) drawCritter(ctx, c, this.groundAt(c.x));
        drawBits(ctx, this);
    };

    Scene.prototype.hit = function (x, y) {
        for (const c of this.critters) {
            const g = this.groundAt(c.x) - c.y;
            if (Math.abs(x - c.x) < c.k.w / 2 + 6 && y > g - c.k.h - LEG - 10 && y < g + 4) return c;
        }
        return null;
    };

    const scenes = [];

    // Hero: the full cast, walking the ticker.
    let hero = null;
    if (heroCanvas) {
        hero = new Scene(heroCanvas, {
            groundAt(x) {
                return this.h - HERO_OVERHANG + TICKER_GAP - (x - this.w / 2) * TICKER_SLOPE;
            },
            populate() {
                const want = this.w < 560 ? 2 : this.w < 900 ? 3 : this.w < 1200 ? 4 : 5;
                while (this.critters.length > want) this.critters.pop();
                const lane = this.w / want;
                ORDER.slice(0, want).forEach((kind, i) => {
                    if (!this.critters[i]) this.critters[i] = makeCritter(kind, lane * (i + 0.5) + rand(-lane, lane) * 0.25);
                    const c = this.critters[i];
                    c.x = clamp(c.x, c.k.w, this.w - c.k.w);
                });
            },
            afterStep() { avoid(this.critters); },
        });
        scenes.push(hero);
    }

    // Closing CTA: one blue slide asleep on the panel, until the button is hovered.
    let nap = null;
    if (napCanvas) {
        nap = new Scene(napCanvas, {
            snoreIn: 0.6,
            wakeFor: 0,
            groundAt() { return this.h - 3; },
            populate() {
                if (!this.critters.length) {
                    const c = makeCritter('presentation', 0);
                    c.state = 'idle';
                    c.stay = true;
                    c.asleep = true;
                    this.critters.push(c);
                }
                this.critters[0].x = this.w * 0.4;
            },
            afterStep(dt) {
                const c = this.critters[0];
                if (c.asleep) {
                    this.snoreIn -= dt;
                    if (this.snoreIn <= 0) {
                        this.snoreIn = 1.3;
                        snore(this, c.x + c.k.w * 0.45, this.groundAt() - LEG - c.k.h - 2);
                    }
                } else if (this.wakeFor > 0) {
                    this.wakeFor -= dt;
                    if (this.wakeFor <= 0 && c.state === 'idle') {
                        c.asleep = true;
                        this.snoreIn = 1.2;
                    }
                }
            },
        });
        nap.wake = function () {
            const c = this.critters[0];
            if (!c) return;
            this.wakeFor = Infinity;
            if (c.asleep) {
                c.asleep = false;
                c.sqTarget = 0;
                // Snores in the air stop being snores.
                this.bits = this.bits.filter(b => b.type !== 'z');
                // Sparkles go above the top of the hop, not in its way.
                if (hop(c, 0.8)) sparkle(this, c.x, this.groundAt() - LEG - c.k.h - 42);
            }
        };
        nap.drowse = function () { this.wakeFor = 2.5; };
        scenes.push(nap);

        const ctaBtn = napCanvas.parentElement.querySelector('.lp-btn');
        if (ctaBtn && !reduceMotion) {
            ctaBtn.addEventListener('pointerenter', () => nap.wake());
            ctaBtn.addEventListener('pointerleave', () => nap.drowse());
            ctaBtn.addEventListener('focus', () => nap.wake());
            ctaBtn.addEventListener('blur', () => nap.drowse());
        }
    }

    // Perches: one critter each, standing on the foot of its canvas. It keeps
    // to its spot, hops now and then of its own accord, and hops for joy when
    // the pointer comes close (not again until the pointer has gone away).
    const perches = perchCanvases.map(canvas => {
        const kind = KINDS[canvas.dataset.kind] ? canvas.dataset.kind : 'quiz';
        const perch = new Scene(canvas, {
            near: false,
            nextHop: rand(3, 7),
            groundAt() { return this.h - 3; },
            populate() {
                if (!this.critters.length) {
                    const c = makeCritter(kind, 0);
                    c.state = 'idle';
                    c.stay = true;
                    this.critters.push(c);
                }
                this.critters[0].x = this.w / 2;
            },
            afterStep(dt) {
                const c = this.critters[0];
                const p = this.pointer;
                const close = !!p && Math.hypot(p.x - c.x, p.y - (this.groundAt() - c.k.h)) < 90;
                if (close && !this.near && hop(c, 1.1)) sparkle(this, c.x, this.groundAt() - LEG - c.k.h - 60);
                this.near = close;
                this.nextHop -= dt;
                if (this.nextHop <= 0) {
                    this.nextHop = rand(4, 9);
                    if (Math.random() < 0.5) hop(c, rand(0.55, 0.8));
                    else c.idleLook = rand(-1, 1);
                }
            },
        });
        scenes.push(perch);
        return perch;
    });

    /* ── Loop ───────────────────────────────────────────── */

    let raf = 0, last = 0;
    let client = null;    // last pointer position, in viewport coordinates

    function frame(now) {
        raf = 0;
        const dt = Math.min(1 / 30, (now - last) / 1000 || 0);
        last = now;
        let running = false;
        for (const s of scenes) {
            if (!s.onScreen || !s.w) continue;
            s.rect = s.canvas.getBoundingClientRect();
            s.pointer = client ? { x: client.x - s.rect.left, y: client.y - s.rect.top } : null;
            s.step(dt);
            s.draw();
            running = true;
        }
        if (hero && hero.pointer && hero.onScreen) {
            const over = hero.hit(hero.pointer.x, hero.pointer.y) && !client.overControl;
            const host = heroCanvas.parentElement;
            const want = over ? 'pointer' : '';
            if (host.style.cursor !== want) host.style.cursor = want;
        }
        if (running && !document.hidden) raf = requestAnimationFrame(frame);
    }

    function kick() {
        if (reduceMotion || raf || document.hidden) return;
        last = performance.now();
        raf = requestAnimationFrame(frame);
    }

    if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(entries => {
            for (const e of entries) {
                const s = scenes.find(sc => sc.canvas === e.target);
                if (s && s.resize()) {
                    if (reduceMotion) s.draw();
                    else kick();
                }
            }
        });
        scenes.forEach(s => ro.observe(s.canvas));
    }

    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
            for (const e of entries) {
                const s = scenes.find(sc => sc.canvas === e.target);
                if (s) s.onScreen = e.isIntersecting;
            }
            kick();
        });
        scenes.forEach(s => io.observe(s.canvas));
    } else {
        scenes.forEach(s => { s.onScreen = true; });
    }

    if (reduceMotion) return;

    document.addEventListener('visibilitychange', kick);

    window.addEventListener('pointermove', e => {
        client = {
            x: e.clientX, y: e.clientY,
            overControl: !!(e.target.closest && e.target.closest('a, button, input, select, textarea, label')),
        };
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => { client = null; });

    // Poke a hero or perched critter and it jumps for joy.
    window.addEventListener('pointerdown', e => {
        if (e.target.closest && e.target.closest('a, button, input, select, textarea, label')) return;
        for (const s of hero ? [hero, ...perches] : perches) {
            if (!s.onScreen || !s.rect) continue;
            const r = s.canvas.getBoundingClientRect();
            const c = s.hit(e.clientX - r.left, e.clientY - r.top);
            if (c && hop(c, 1.35)) {
                confetti(s, c.x, s.groundAt(c.x) - LEG - c.k.h, 16);
                return;
            }
        }
    });

    // When the headline flips to a word, that word's critter hops.
    const flip = document.getElementById('lp-flip');
    if (flip && hero) {
        let current = '';
        new MutationObserver(() => {
            const word = flip.querySelector('.lp-flip-word.is-in');
            const text = word ? word.textContent.trim() : '';
            if (text === current) return;
            current = text;
            const c = hero.critters.find(cr => cr.kind === text);
            if (c && hero.onScreen && hop(c, 0.95)) {
                sparkle(hero, c.x, hero.groundAt(c.x) - LEG - c.k.h - 58);
            }
        }).observe(flip, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }
})();
